import { FREE_PLAN_ID } from '@/features/billing/constants';
import type { PlanName } from '@/features/billing/types/billing.types';

/**
 * The plan names the pricing page renders, which are deliberately not the API's
 * `PlanName`.
 *
 * UPT-3355 renamed the tier-3 plan's copy from Enterprise to Scale without
 * touching its plan id, and the rename is still rolling out — some environments
 * keep serving `Enterprise` for it. `Enterprise` is now also the name of the new
 * contact-sales card, so the two would collide and put two identically-labelled
 * cards side by side. Everything the page shows goes through
 * {@link getDisplayPlanName} to make that impossible.
 */
export type DisplayPlanName = 'Free' | 'Solo' | 'Team' | 'Scale' | 'Enterprise';

/** Display name of the contact-sales card. It has no product, so it never comes from the API. */
export const ENTERPRISE_PLAN_NAME: Extract<DisplayPlanName, 'Enterprise'> =
  'Enterprise';

/**
 * The names a plan that came from the API can display as. Enterprise is excluded
 * by construction: it has no product, so nothing the API returns can map to it.
 */
export type ApiDisplayPlanName = Exclude<DisplayPlanName, 'Enterprise'>;

/**
 * Maps an API plan name to the name shown on the card.
 *
 * `Enterprise` from the API *is* the tier-3 self-serve plan, so it renders as
 * Scale.
 */
export const getDisplayPlanName = (name: PlanName): ApiDisplayPlanName =>
  name === 'Enterprise' ? 'Scale' : name;

interface PlanWithId {
  id: number;
}

export interface SplitPlans<T extends PlanWithId> {
  /** Rendered as a full-width block beneath the card row. */
  freePlan: T | null;
  /** Rendered in the card grid, in API order. */
  paidPlans: T[];
}

/**
 * Separates Free from the plans that stay in the card grid.
 *
 * Free leaves the row because it is the only plan whose pitch is what you *don't*
 * get: its feature list is long and reads better across four columns than down
 * one. The API simply omits the Free product once a user is on a paid plan, so
 * `freePlan` is null for them and the block never renders.
 */
export const splitPlans = <T extends PlanWithId>(
  plans: T[],
): SplitPlans<T> => ({
  freePlan: plans.find(plan => plan.id === FREE_PLAN_ID) ?? null,
  paidPlans: plans.filter(plan => plan.id !== FREE_PLAN_ID),
});
